<template>
  <PropertyOnboardingCard>
    <template #header>
      <View justify="end">
        <Button variant="ghost" :to="portfolioRoute(portfolio, 'buildings')">Exit</Button>
      </View>
    </template>

    <template #body>
      <View direction="column" gap="md" class="content">
        <Text variant="h1">Tell us about the building</Text>

        <FormGroup>
          <FormField
            label="Name"
            hint="Optional. Left blank, the building goes by its street address."
            :error="form.fieldErrors('name')"
          >
            <Input v-model="form.fields.name" :placeholder="form.fields.location?.address" maxlength="120" />
          </FormField>

          <FormField label="Address" :error="form.fieldErrors('location')">
            <AutocompleteAddress v-model="form.fields.location" />
          </FormField>

          <Map :coordinates="coordinates" />

          <NewUnitPortfolioField ref="portfolioFieldRef" v-model="portfolioId" :initial="portfolio" last />
        </FormGroup>
      </View>
    </template>

    <template #footer>
      <Button
        class="create-button"
        :loading="form.submitting"
        :disabled="form.validation.$invalid || isBusy"
        @click="create"
      >
        Create building
      </Button>
    </template>
  </PropertyOnboardingCard>
</template>

<script setup lang="ts">
import { useQueryCache } from '@pinia/colada';
import { helpers, required } from '@vuelidate/validators';
import { AxiosError } from 'axios';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { Button, FormField, FormGroup, Input, Text, View, useAlertsStore, useForm } from '@tallo/design-system';
import { companyPortfoliosQueryKey } from '@tallo/investor/company';
import { buildingRoute, portfolioRoute } from '@tallo/investor/navigation';
import {
  AutocompleteAddress,
  NewUnitPortfolioField,
  useAddressLookup,
  useCreateBuilding,
} from '@tallo/investor/property';
import { AddressDto, Map } from '@tallo/property';

import PropertyOnboardingCard from '../property-onboarding/components/property-onboarding-card.vue';

const route = useRoute();
const router = useRouter();
const queryCache = useQueryCache();
const { mutateAsync: createBuilding } = useCreateBuilding();
const portfolioFieldRef = ref<InstanceType<typeof NewUnitPortfolioField>>();
const portfolioId = ref('');

const form = useForm<{ name: string; location: AddressDto | null }>(
  { name: '', location: null },
  { location: { required: helpers.withMessage('Address is required', required) } },
);

// "Add new listing" from a page showing one portfolio, or Unassigned, carries it here
const portfolio = computed(() => (typeof route.query.portfolio === 'string' ? route.query.portfolio : undefined));
const { coordinates, isLoading: isLookingUp } = useAddressLookup(() => form.fields.location);
const isBusy = computed(() => isLookingUp.value || Boolean(portfolioFieldRef.value?.isLoading));

async function create() {
  try {
    const building = await form.submit((fields) =>
      createBuilding({
        name: fields.name.trim() || undefined,
        location: { ...fields.location, ...coordinates.value },
        portfolioId: portfolioId.value || undefined,
      }),
    );

    await router.push(buildingRoute(building.id));
  } catch (error) {
    // A rejected field shows on the form; anything else from the server (a portfolio refused
    // meanwhile) is said here, and the screen stays as it is
    if (error instanceof AxiosError && !error.response?.data?.validationErrors) {
      useAlertsStore().catchError(error);
      // A portfolio refused here may be gone or no longer the viewer's; the choices re-read
      queryCache.invalidateQueries({ key: [companyPortfoliosQueryKey] }).catch(() => undefined);
    }
  }
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

// The wizard steps' column, so this screen reads like the address step
.content {
  max-width: 32rem;
  margin: 0 auto;
  padding-block: var(--t-spacing-xxl);

  @include mobile {
    padding-block: var(--t-spacing-lg);
  }
}

.create-button {
  margin-inline-start: auto;
}
</style>
